import React, { useState, useEffect } from 'react';
import { Box, Button, Container, TextField, Typography, Paper, Alert, Divider } from '@mui/material';
import GoogleLogo from '../components/common/GoogleLogo';
import axios from 'axios';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import { useAuth } from '../context/AuthContext';
import useSEO from '../hooks/useSEO';
import { API_URL } from '../config/api';
import RecaptchaNotice from '../components/common/RecaptchaNotice';

declare global {
  interface Window {
    grecaptcha: any;
  }
}

const RECAPTCHA_SITE_KEY = '6LeDbCEsAAAAANw-bAoYe_8Ldw4phCZQLjkkl_Hw';

const SignupPage: React.FC = () => {
  useSEO({ title: 'ثبت‌نام', noindex: true });
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [recaptchaReady, setRecaptchaReady] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/user');
    }
  }, [isAuthenticated, navigate]);

  // Load reCAPTCHA v3 script
  useEffect(() => {
    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      window.grecaptcha.ready(() => {
        setRecaptchaReady(true);
      });
    };
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setSuccess(null);

    if (password.length < 10) {
      setError('رمز عبور باید حداقل ۱۰ کاراکتر باشد.');
      return;
    }

    if (password !== confirmPassword) {
      setError('رمز عبور و تکرار آن یکسان نیستند.');
      return;
    }

    if (!recaptchaReady) {
      setError('لطفاً چند لحظه صبر کنید و دوباره تلاش کنید.');
      return;
    }

    try {
      setSubmitting(true);

      // Execute reCAPTCHA v3
      const token = await window.grecaptcha.execute(RECAPTCHA_SITE_KEY, { action: 'signup' });

      const response = await axios.post(`${API_URL}/auth/signup`, {
        fullName,
        email,
        password,
        recaptchaToken: token,
      });

      setSuccess(
        'حساب کاربری شما ساخته شد. برای فعال‌سازی، روی لینک تایید در ایمیلی که برایتان ارسال شد کلیک کنید. اگر ایمیل را در صندوق ورودی نمی‌بینید، لطفاً پوشه Spam / Junk را بررسی کنید و آن را «Not spam» علامت بزنید.'
        + '\n\n'
        + 'Your account has been created. Please click the verification link in the email we just sent you to activate it. If it isn\'t in your inbox, check your Spam / Junk folder and mark it as "Not spam".'
      );

      setFullName('');
      setEmail('');
      setPassword('');
      setConfirmPassword('');
    } catch (err: any) {
      if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else {
        setError('ثبت‌نام ناموفق بود. لطفاً دوباره تلاش کنید.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const GoogleButton = styled(Button)({
    backgroundColor: '#fff',
    color: 'rgba(0, 0, 0, 0.87)',
    border: '1px solid #dadce0',
    borderRadius: '4px',
    textTransform: 'none',
    padding: '10px 24px',
    fontSize: '14px',
    fontWeight: 500,
    '&:hover': {
      backgroundColor: '#f8f9fa',
      boxShadow: '0 1px 3px 1px rgba(66,64,67,.15)',
    },
    '& .MuiButton-startIcon': {
      marginRight: '10px',
      marginLeft: '2px',
    },
  });

  const SignupButton = styled(Button)({
    backgroundColor: '#1a73e8',
    color: 'white',
    textTransform: 'none',
    padding: '10px 24px',
    fontSize: '14px',
    fontWeight: 500,
    '&:hover': {
      backgroundColor: '#1557b0',
      boxShadow: '0 1px 3px 1px rgba(66,64,67,.15)',
    },
  });

  return (
    <Container component="main" maxWidth="xs">
      <Paper
        elevation={3}
        sx={{
          p: { xs: 3, md: 4 },
          mt: { xs: 4, md: 8 },
          borderRadius: '8px',
          // 14px text, in line with the rest of the site. Typed text stays
          // 16px on phones: iOS zooms the page on smaller inputs.
          '& .MuiInputBase-input': { fontSize: { xs: '16px', md: '14px' } },
          '& .MuiInputLabel-root': { fontSize: '14px' },
          '& .MuiInputLabel-shrink': { fontSize: '16px' },
          '& .MuiButton-root': { fontSize: '14px' },
          '& .MuiButton-sizeSmall': { fontSize: '13px' },
          '& .MuiAlert-message': { fontSize: '14px' },
          '& .MuiDivider-root': { fontSize: '13px' },
        }}
      >
        <Typography component="h1" variant="h6" align="center" gutterBottom sx={{ fontSize: '1.15rem', fontWeight: 700, mb: 2 }}>
          ایجاد حساب کاربری
        </Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {/* One paragraph per line, each with its own text direction (dir="auto"),
            so Persian lines run right-to-left and English lines left-to-right. */}
        {success && (
          <Alert severity="success" sx={{ mb: 2, '& .MuiAlert-message': { width: '100%' } }}>
            {success.split('\n').filter(line => line.trim()).map((line, i) => (
              <Box key={i} dir="auto" sx={{ textAlign: 'start', mt: i > 0 ? 1 : 0 }}>{line}</Box>
            ))}
          </Alert>
        )}

        <GoogleButton
          fullWidth
          variant="outlined"
          startIcon={<GoogleLogo />}
          href={`${API_URL}/auth/google`}
          sx={{ mb: 3 }}
        >
          ادامه با گوگل
        </GoogleButton>

        <Divider sx={{ mb: 3 }}>یا</Divider>

        <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1 }}>
          <TextField
            size="small"
            margin="normal"
            required
            fullWidth
            id="fullName"
            label="نام و نام خانوادگی"
            name="fullName"
            autoComplete="name"
            autoFocus
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            size="small"
            margin="normal"
            required
            fullWidth
            id="email"
            label="ایمیل"
            name="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            size="small"
            margin="normal"
            required
            fullWidth
            name="password"
            label="رمز عبور"
            type="password"
            id="password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            helperText="Password must be at least 10 characters long"
            sx={{ mb: 2 }}
          />
          <TextField
            size="small"
            margin="normal"
            required
            fullWidth
            name="confirmPassword"
            label="تکرار رمز عبور"
            type="password"
            id="confirmPassword"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            sx={{ mb: 2 }}
          />

          <RecaptchaNotice />

          <SignupButton
            type="submit"
            fullWidth
            variant="contained"
            disabled={submitting || !recaptchaReady}
            sx={{ mt: 3, mb: 2 }}
          >
            {submitting ? 'در حال ایجاد حساب...' : !recaptchaReady ? 'در حال بارگذاری...' : 'ثبت‌نام'}
          </SignupButton>
        </Box>
        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary" dir="rtl">
            قبلاً ثبت‌نام کرده‌اید؟{' '}
            <Button
              component={RouterLink}
              to="/login"
              size="small"
              sx={{ textTransform: 'none', fontWeight: 500 }}
            >
              ورود
            </Button>
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
};

export default SignupPage;
